/*
 * Copyright © 2026 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { Micro } from 'effect';
import { createRandomString, createState } from '@forgerock/sdk-utilities';
import { oidcApi } from './oidc.api.js';
import { decodeJwt } from 'jose/jwt/decode';
// ─── Storage read ─────────────────────────────────────────────────────────────
export const readStoredIdTokenµ = (storageClient) => Micro.tryPromise({
    try: () => storageClient.get(),
    catch: () => ({
        error: 'storage_error',
        message: 'Failed to read tokens from storage',
        type: 'argument_error',
    }),
}).pipe(Micro.map((tokens) => (tokens && 'idToken' in tokens ? tokens.idToken : null)));
// ─── Dispatch ────────────────────────────────────────────────────────────────
export const dispatchSessionCheckIframeµ = (store, url, responseType) => Micro.tryPromise({
    try: () => store.dispatch(oidcApi.endpoints.sessionCheckIframe.initiate({ url, responseType })),
    catch: (err) => ({
        error: 'dispatch_error',
        message: err instanceof Error ? err.message : 'Failed to dispatch session check',
        type: 'network_error',
    }),
}).pipe(Micro.flatMap((result) => {
    if ('error' in result && result.error) {
        const errData = result.error;
        return Micro.fail({
            error: errData.data?.error ?? 'session_check_error',
            message: errData.data?.message ?? 'An error occurred during session check',
            type: errData.data?.type ?? 'network_error',
        });
    }
    const { params } = result.data;
    return Micro.succeed(params);
}));
export const dispatchSessionCheckFetchµ = (store, url) => Micro.tryPromise({
    try: () => store.dispatch(oidcApi.endpoints.sessionCheckFetch.initiate({ url })),
    catch: (err) => ({
        error: 'dispatch_error',
        message: err instanceof Error ? err.message : 'Failed to dispatch session check',
        type: 'network_error',
    }),
}).pipe(Micro.flatMap((result) => {
    if ('error' in result && result.error) {
        const errData = result.error;
        return Micro.fail({
            error: errData.data?.error ?? 'login_required',
            message: errData.data?.message ?? 'The request requires login.',
            type: errData.data?.type ?? 'auth_error',
        });
    }
    return Micro.void;
}));
// ─── Response validation ──────────────────────────────────────────────────────
export const validateSessionCheckResponseµ = (iframeParams, state, nonce, subject) => {
    return Micro.gen(function* () {
        if (iframeParams.state !== state) {
            return yield* Micro.fail({
                error: 'state_mismatch',
                message: 'State parameter in response does not match the expected value',
                type: 'auth_error',
            });
        }
        const idToken = iframeParams.id_token;
        if (!idToken) {
            return yield* Micro.fail({
                error: 'no_id_token',
                message: 'No id_token found in iframe response',
                type: 'auth_error',
            });
        }
        const claims = yield* Micro.try({
            try: () => decodeJwt(idToken),
            catch: () => ({
                error: 'invalid_jwt',
                message: 'Failed to decode id_token JWT payload',
                type: 'auth_error',
            }),
        });
        if (claims.nonce !== nonce) {
            return yield* Micro.fail({
                error: 'nonce_mismatch',
                message: 'Nonce in id_token does not match the expected value',
                type: 'auth_error',
            });
        }
        if (subject !== undefined && claims.sub !== subject) {
            return yield* Micro.fail({
                error: 'subject_mismatch',
                message: 'Subject claim in id_token does not match the expected value',
                type: 'auth_error',
            });
        }
        return claims;
    });
};
// ─── Param builders ───────────────────────────────────────────────────────────
export const buildNoneUrl = (endpoint, config, storedIdToken, redirectUri, options) => {
    const params = new URLSearchParams({
        prompt: 'none',
        response_type: 'none',
        client_id: config.clientId,
        scope: options?.scope ?? 'openid',
        ...(redirectUri ? { redirect_uri: redirectUri } : {}),
        id_token_hint: storedIdToken,
    });
    return `${endpoint}?${params.toString()}`;
};
export const buildIdTokenUrl = (endpoint, config, storedIdToken, redirectUri, options) => {
    const nonce = createRandomString(32);
    const state = createState();
    const params = new URLSearchParams({
        prompt: 'none',
        response_type: 'id_token',
        client_id: config.clientId,
        redirect_uri: redirectUri,
        scope: options?.scope ?? 'openid',
        nonce,
        state,
        ...(storedIdToken ? { id_token_hint: storedIdToken } : {}),
    });
    return { url: `${endpoint}?${params.toString()}`, nonce, state };
};
// ─── None mode ───────────────────────────────────────────────────────────────
export const sessionCheckNoneµ = (wellknown, config, store, storageClient, log, options) => {
    return readStoredIdTokenµ(storageClient).pipe(Micro.flatMap((storedIdToken) => {
        if (!storedIdToken) {
            return Micro.fail({
                error: 'no_id_token_hint',
                message: 'response_type=none requires a stored id_token; authenticate first',
                type: 'argument_error',
            });
        }
        const redirectUri = options?.redirectUri ?? config.redirectUri;
        const url = buildNoneUrl(wellknown.authorization_endpoint, config, storedIdToken, redirectUri, options);
        // iframe path: AM redirects back to redirect_uri; resolve on landing
        // fetch path: no redirect_uri, AM returns 204 (valid) or 400 (invalid)
        return redirectUri
            ? dispatchSessionCheckIframeµ(store, url, 'none')
            : dispatchSessionCheckFetchµ(store, url);
    }), Micro.tap(() => log.debug('Session check (none) completed successfully')), Micro.map(() => ({ responseType: 'none' })));
};
// ─── IdToken mode ─────────────────────────────────────────────────────────────
export const sessionCheckIdTokenµ = (wellknown, config, store, storageClient, log, options) => {
    const redirectUri = options?.redirectUri ?? config.redirectUri;
    if (!redirectUri) {
        return Micro.fail({
            error: 'missing_redirect_uri',
            message: 'redirect_uri is required for session check with response_type=id_token',
            type: 'argument_error',
        });
    }
    return readStoredIdTokenµ(storageClient).pipe(Micro.flatMap((storedIdToken) => {
        const { url, nonce, state } = buildIdTokenUrl(wellknown.authorization_endpoint, config, storedIdToken, redirectUri, options);
        return dispatchSessionCheckIframeµ(store, url, 'id_token').pipe(Micro.flatMap((iframeParams) => validateSessionCheckResponseµ(iframeParams, state, nonce, options?.subject)));
    }), Micro.tap(() => log.debug('Session check (id_token) completed successfully')), Micro.map((claims) => ({ responseType: 'id_token', claims })));
};
//# sourceMappingURL=session.micros.js.map